/**
 * Adapter dla wewnetrznego asystenta AI (WiseAi).
 *
 * Endpointy zwracaja struktury DTO z naszego backendu — adapter mapuje je na ksztalt
 * zgodny z `orionEndpoints/`, dzieki czemu `useHydraAIAssistant.ts` nie wymaga zmian.
 *
 * Backend: Wise/AssistantAi/ApiUi/* — routes pod /api/ui/assistant-ai/*
 * Kontrakty:
 *  - POST /new-thread => Common200Form: { status, data: { id, ... } }
 *  - POST /add-message/{threadId}/{assistant}?sessionId=... => HTTP 204
 *  - GET  /get-thread-content/{threadId}/{includeDetails}/{lastTurnId}/{contentHash}
 *      => { thread_id, state, current_turn_id, current_assistant_name,
 *           content_hash, is_done, messages: [{ id, role, content, turn_id, ... }] }
 *  - POST /update-thread-context/{threadId} => 200 form
 *  - POST /cancel-processing/{threadId} => 200 form
 *  - POST /remove-last-message/{threadId} => 200 form
 */

import { wiseAiAxios } from '../wiseAiApi';
import {
  CreateThreadRequest,
  UpdateThreadContextRequest,
  AddMessageRequest,
  ChatResponse,
  ChatAttachment,
  WiseAiStructuredMessage,
  WiseAiToolCall
} from '../orionEndpoints/orionTypes';

interface WiseAiMessageRaw {
  id?: string | null;
  role?: string | null;
  content?: string | null;
  turn_id?: string | null;
  finalized?: boolean | null;
  tool_call_id?: string | null;
  tool_calls?: unknown[] | null;
  created_at?: string | null;
}

interface WiseAiThreadContentRaw {
  thread_id?: string | null;
  state?: string | null;
  current_turn_id?: string | null;
  current_assistant_name?: string | null;
  content_hash?: string | null;
  is_done?: boolean | null;
  messages?: WiseAiMessageRaw[] | null;
}

interface Common200FormResponse<T> {
  status?: number;
  data?: T;
}

interface NewThreadData {
  id?: string;
}

const NEUTRAL_TURN = 'wiseai-turn';

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Lekki konwerter markdown -> HTML pokrywajacy formaty zwracane przez asystenta:
 * naglowki, **bold**, *italic*, `code`, linki, listy ul/ol, akapity (pusta linia),
 * pojedyncze lamania (<br>). Trzymamy go w adapterze zeby nie pociagac ciezkiego
 * react-markdown w runtime parsera.
 */
const renderMarkdown = (markdown: string): string => {
  if (!markdown) return '';
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');

  const inlineFormat = (text: string): string => {
    let out = escapeHtml(text);
    // inline code
    out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
    // bold **text**
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // italic *text* (ale nie wewnatrz juz przekonwertowanych)
    out = out.replace(/(^|[\s(])\*([^*]+)\*/g, '$1<em>$2</em>');
    // linki [tekst](url)
    out = out.replace(
      /\[([^\]]+)\]\((https?:[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    return out;
  };

  const out: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    out.push(`<p>${paragraph.map(inlineFormat).join('<br>')}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      closeList();
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length + 2; // # -> h3
      out.push(`<h${level}>${inlineFormat(heading[2])}</h${level}>`);
      continue;
    }
    const ulItem = line.match(/^\s*[-*]\s+(.*)$/);
    const olItem = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ulItem || olItem) {
      flushParagraph();
      const wanted: 'ul' | 'ol' = ulItem ? 'ul' : 'ol';
      if (listType !== wanted) {
        closeList();
        out.push(`<${wanted}>`);
        listType = wanted;
      }
      const itemText = (ulItem ? ulItem[1] : (olItem as RegExpMatchArray)[1]);
      out.push(`<li>${inlineFormat(itemText)}</li>`);
      continue;
    }
    closeList();
    paragraph.push(line);
  }
  flushParagraph();
  closeList();
  return out.join('');
};

/**
 * Sklada strukturalne wiadomosci WiseAi w HTML chunk zgodny z parserem ChatBase
 * (`simpleParseMessages`). Parser wymaga:
 *  - `<!-- turn-id:UUID -->` przed kazda turą,
 *  - `.user-message` dla wiadomosci usera,
 *  - reszta HTML traktowana jako tresc asystenta.
 *
 * UWAGA: parser oczekuje turn-id w postaci `[a-f0-9-]+` (UUID-like). Nasz backend
 * uzywa stringow typu `turn_20260601_caeb` — normalizujemy je do hex-friendly
 * postaci aby parser potraktowal je jak turn-id.
 */
const normalizeTurnId = (raw: string | null | undefined): string => {
  if (!raw) return NEUTRAL_TURN;
  return raw.toLowerCase().replace(/[^a-f0-9-]/g, '-');
};

const buildChunkFromMessages = (messages: WiseAiMessageRaw[]): string => {
  const byTurn = new Map<string, WiseAiMessageRaw[]>();
  messages.forEach((m) => {
    const turn = normalizeTurnId(m.turn_id);
    if (!byTurn.has(turn)) byTurn.set(turn, []);
    byTurn.get(turn)?.push(m);
  });

  return Array.from(byTurn.entries())
    .map(([turnId, turnMessages]) => {
      const htmlParts = turnMessages.map((m) => {
        const role = (m.role || '').toLowerCase();
        const content = m.content || '';
        if (role === 'user') {
          return `<div class="user-message"><p>${escapeHtml(content)}</p></div>`;
        }
        if (role === 'assistant') {
          return `<div class="assistant-message">${renderMarkdown(content)}</div>`;
        }
        // tool / toolCallRequest / log — szczegoly developerskie (filtrowane gdy includeDetails=false)
        return `<details class="agent-message"><summary>${escapeHtml(role)}</summary><pre>${escapeHtml(
          content
        )}</pre></details>`;
      });
      return `<!-- turn-id:${turnId} -->${htmlParts.join('')}`;
    })
    .join('');
};

export const createThread = async (data: CreateThreadRequest): Promise<string> => {
  const ctx = data.context || {};
  const body = {
    // Nazwa asystenta z kontekstu (np. `trader` w panelu handlowca, `wiseb2b` w sklepie).
    // Fallback `wiseb2b` zachowuje wsteczna kompatybilnosc gdyby kontekst nie zostal podany.
    selectedAssistantName: ctx.selectedAssistantName ?? 'wiseb2b',
    currentModuleName: ctx.currentModuleName ?? '',
    currentPage: ctx.currentPage ?? '',
    fullUrl: ctx.fullUrl ?? '',
    draftMessage: '',
    title: ''
  };
  const response = await wiseAiAxios.post<Common200FormResponse<NewThreadData>>('/new-thread', body);
  return response.data?.data?.id ?? '';
};

export const updateThreadContext = async (
  threadId: string,
  data: UpdateThreadContextRequest
): Promise<void> => {
  const ctx = data.context || {};
  await wiseAiAxios.post(`/update-thread-context/${threadId}`, {
    currentModuleName: ctx.currentModuleName ?? '',
    currentPage: ctx.currentPage ?? '',
    fullUrl: ctx.fullUrl ?? ''
  });
};

export const addMessage = async (
  threadId: string,
  assistant: string,
  data: AddMessageRequest
): Promise<void> => {
  const sessionId = data.urlParameters?.session_id;
  const url = sessionId
    ? `/add-message/${threadId}/${encodeURIComponent(assistant)}?sessionId=${encodeURIComponent(sessionId)}`
    : `/add-message/${threadId}/${encodeURIComponent(assistant)}`;
  const body: { content: string; hiddenContext?: string } = {
    content: data.message ?? ''
  };
  // Hidden context — backend dokleja jako system message tylko w tej turze, nie persist.
  if (data.hiddenContext && data.hiddenContext.length > 0) {
    body.hiddenContext = data.hiddenContext;
  }
  await wiseAiAxios.post(url, body);
};

const mapToStructured = (m: WiseAiMessageRaw): WiseAiStructuredMessage => ({
  id: m.id ?? '',
  role: m.role ?? '',
  content: m.content ?? '',
  turnId: m.turn_id ?? '',
  finalized: m.finalized ?? true,
  toolCallId: m.tool_call_id ?? null,
  toolCalls: (m.tool_calls as WiseAiToolCall[] | null | undefined) ?? null,
  createdAt: m.created_at ?? ''
});

export const getThreadContent = async (
  threadId: string,
  includeDetails: number,
  lastTurnId: string,
  contentHash: string
): Promise<ChatResponse> => {
  const safeLastTurnId = lastTurnId && lastTurnId !== 'none' ? lastTurnId : '0';
  const safeContentHash = contentHash && contentHash !== 'none' ? contentHash : '0';
  // includeDetails=1 wymuszamy, bo `tool_call_request` i `tool` sa potrzebne do
  // dedykowanych widokow (ProductsResultView, ToolCallCard). Bez tego backend
  // odsiewa szczegoly i UI dostaje tylko user/assistant.
  const response = await wiseAiAxios.get<WiseAiThreadContentRaw>(
    `/get-thread-content/${threadId}/1/${encodeURIComponent(
      safeLastTurnId
    )}/${encodeURIComponent(safeContentHash)}`
  );
  const raw = response.data ?? {};
  const messages = raw.messages ?? [];
  const chunk = messages.length > 0 ? buildChunkFromMessages(messages) : '';
  const hasIncrementalBase = !!lastTurnId && lastTurnId !== 'none' && lastTurnId !== '0';
  return {
    chunk,
    isDone: !!raw.is_done,
    fromTurnId: hasIncrementalBase ? lastTurnId : null,
    currentAssistantName: raw.current_assistant_name ?? '',
    // `content_hash` z backendu — propagujemy do nastepnego pollingu zeby
    // optymalizator no-change na backendzie zwracal pusty `messages: []` gdy
    // nic sie nie zmienilo (oszczedza SELECT na chat_messages).
    serverContentHash: raw.content_hash ?? null,
    // WAZNE: zwracamy `undefined` zamiast pustej tablicy gdy backend nie ma
    // nowych wiadomosci (no-change albo nadal `processing` bez nowych chunkow).
    // Bez tego hook `useHydraAIAssistant` nadpisywal `structuredMessages` pustka,
    // co powodowalo "znikniecie" rozmowy w UI podczas pollingu.
    structuredMessages: messages.length > 0 ? messages.map(mapToStructured) : undefined
  };
};

export const cancelProcessing = async (threadId: string): Promise<void> => {
  await wiseAiAxios.post(`/cancel-processing/${threadId}`, {});
};

export const removeLastMessage = async (threadId: string): Promise<unknown> => {
  const response = await wiseAiAxios.post(`/remove-last-message/${threadId}`, {});
  return response.data;
};

// Zalaczniki w WiseAi MVP nie sa wspierane — zwracamy bezpieczne no-opy aby
// zachowac kontrakt z `useHydraAIAssistant.ts` bez zmian w UI.
export const addAttachment = async (
  _threadId: string,
  _data: unknown
): Promise<string> => '';

export const removeAttachment = async (
  _threadId: string,
  _attachmentId: string
): Promise<void> => {
  // no-op
};

export const getAttachments = async (_threadId: string): Promise<ChatAttachment[]> => [];
